import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInvoices, calcTotals } from '../context/InvoicesContext';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate, fmtDateFull, daysUntil } from '../lib/format';
import { getContentStatusList, toggleContentStatus, isDealInvoiceStatusDone, suggestedInvoiceAmount, dealTalentIds } from '../lib/deals';
import { Plus, Search, FileText, Send, CheckCircle2, AlertCircle, Sparkles, Briefcase, Check, Filter, LayoutGrid, List, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import InitialsAvatar from '../components/InitialsAvatar';
import DealsInvoicingTable from '../components/invoices/DealsInvoicingTable';
import ActiveInvoicesButton from '../components/invoices/ActiveInvoicesButton';
import InvoicingKanban from '../components/invoices/InvoicingKanban';
import { api } from '../lib/api';
import { toast } from 'sonner';

const StatusPill = ({ status, statuses }) => {
  const s = statuses.find((x) => x.id === status);
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 capitalize">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
      {s?.label}
    </span>
  );
};

const StatCard = ({ label, value, hint, Icon, tint, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
      {Icon && <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-3.5 h-3.5" /></div>}
    </div>
    <div className={`mt-1 text-2xl font-semibold ${accent || 'text-slate-900'}`}>{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
  </div>
);

const Invoices = () => {
  const { invoices, statuses, brandMap, talentMap, updateInvoice, loading } = useInvoices();
  const { deals, updateDeal, talent } = useDeals();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [talentId, setTalentId] = useState('all');
  const [invoiceView, setInvoiceView] = useState(() => {
    try { return localStorage.getItem('invoices.view') || 'list'; } catch { return 'list'; }
  });
  useEffect(() => { try { localStorage.setItem('invoices.view', invoiceView); } catch {} }, [invoiceView]);
  const [tab, setTab] = useState('talent');  // 'talent' | 'client'
  const [readyClients, setReadyClients] = useState([]);
  const [readyOpen, setReadyOpen] = useState(true);
  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const loadReadyClients = async () => {
    try {
      const { data } = await api.get('/clients/ready-to-invoice', { params: { month: currentMonth } });
      setReadyClients(data || []);
    } catch { /* non-critical, suggestions just stay empty */ }
  };
  useEffect(() => { loadReadyClients(); }, [currentMonth]);

  React.useEffect(() => {
    if (!isManager) return;
    invoices.forEach((inv) => {
      if (inv.status === 'sent' && inv.due_date && daysUntil(inv.due_date) < 0) {
        updateInvoice(inv.id, { status: 'overdue' });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.length]);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      // Split: client invoices have client_id set; everything else is talent/deal invoicing
      if (tab === 'client' && !inv.client_id) return false;
      if (tab === 'talent' && inv.client_id) return false;
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        const b = brandMap[inv.brand_id];
        const t = talentMap[inv.talent_id];
        return (
          (inv.number || '').toLowerCase().includes(s) ||
          (b?.name || '').toLowerCase().includes(s) ||
          (t?.name || '').toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [invoices, q, statusFilter, brandMap, talentMap, tab]);

  const totals = useMemo(() => {
    const sum = (arr) => arr.reduce((s, inv) => s + calcTotals(inv).total, 0);
    return {
      outstanding: sum(invoices.filter((i) => ['sent', 'overdue'].includes(i.status))),
      paid: sum(invoices.filter((i) => i.status === 'paid')),
      overdue: sum(invoices.filter((i) => i.status === 'overdue')),
      draft: sum(invoices.filter((i) => i.status === 'draft')),
    };
  }, [invoices]);

  // Talent-tab suggestions: deals ready to invoice, not yet invoiced, not manually muted.
  const talentSuggestions = useMemo(
    () => deals.filter((d) => {
      if (suggestedInvoiceAmount(d) <= 0) return false;
      if (!d.ready_to_invoice) return false;
      if (d.invoiced_at) return false;
      const dealInvs = invoices.filter((i) => i.deal_id === d.id);
      const splits = d.invoice_splits || [];
      const isInvoiced = d.split_invoicing
        ? (splits.length > 0 && splits.every((s) => s.status === 'invoiced'))
        : (dealInvs.length > 0 || isDealInvoiceStatusDone(d.invoice_status));
      return !isInvoiced;
    }),
    [deals, invoices]
  );

  const muteTalentSuggestion = async (dealId) => {
    try {
      await updateDeal(dealId, { invoiced_at: new Date().toISOString().slice(0, 10) });
      toast.success('marked invoiced — suggestion muted');
    } catch { toast.error('failed to mute suggestion'); }
  };

  const muteClientSuggestion = async (client) => {
    try {
      await api.patch(`/clients/${client.client_id}`, { manually_invoiced_months: [...new Set([...(client.manually_invoiced_months || []), currentMonth])] });
      setReadyClients((cs) => cs.filter((c) => c.client_id !== client.client_id));
      toast.success('marked invoiced — suggestion muted');
    } catch { toast.error('failed to mute suggestion'); }
  };

  // Talent-tab count: number of deals shown in DealsInvoicingTable (ready to invoice + value > 0,
  // then filtered by search + invoiced/to_invoice status filter).
  const talentTabCount = useMemo(() => {
    return deals.filter((d) => {
      if (suggestedInvoiceAmount(d) <= 0) return false;
      if (!d.ready_to_invoice) return false;
      if (talentId !== 'all' && !dealTalentIds(d).includes(talentId)) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        const b = brandMap[d.brand_id];
        const tNames = dealTalentIds(d).map((id) => talentMap[id]?.name || '').join(' ');
        if (!`${d.title || ''} ${b?.name || ''} ${tNames}`.toLowerCase().includes(s)) return false;
      }
      if (statusFilter !== 'all') {
        const dealInvs = invoices.filter((i) => i.deal_id === d.id);
        const splits = d.invoice_splits || [];
        const isInvoiced = d.split_invoicing
          ? (splits.length > 0 && splits.every((s) => s.status === 'invoiced'))
          : (dealInvs.length > 0 || isDealInvoiceStatusDone(d.invoice_status));
        const isPaid = dealInvs.some((i) => i.status === 'paid') || d.invoice_status === 'paid_out';
        if (statusFilter === 'paid' && !isPaid) return false;
        if (statusFilter === 'invoiced' && (!isInvoiced || isPaid)) return false;
        if (statusFilter === 'to_invoice' && (isInvoiced || isPaid)) return false;
      }
      return true;
    }).length;
  }, [deals, brandMap, talentMap, invoices, q, statusFilter, talentId]);

  // Client-tab count: number of raised client invoices matching the search + status filter.
  const clientTabCount = useMemo(() => {
    return invoices.filter((inv) => {
      if (!inv.client_id) return false;
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        const b = brandMap[inv.brand_id];
        const t = talentMap[inv.talent_id];
        return (
          (inv.number || '').toLowerCase().includes(s) ||
          (b?.name || '').toLowerCase().includes(s) ||
          (t?.name || '').toLowerCase().includes(s)
        );
      }
      return true;
    }).length;
  }, [invoices, brandMap, talentMap, q, statusFilter]);

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">invoicing</h1>
          <p className="text-sm text-slate-500 mt-1">{isManager ? 'create, send and chase invoices without leaving LVE Social.' : 'invoices we’ve raised on your behalf.'}</p>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <ActiveInvoicesButton />
            <Link to="/invoicing/new">
              <Button className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> new invoice
              </Button>
            </Link>
          </div>
        )}
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="outstanding" value={fmtMoney(totals.outstanding)} hint="awaiting payment" Icon={Send} tint="bg-sky-50 text-sky-600" />
        <StatCard label="overdue" value={fmtMoney(totals.overdue)} hint="needs chasing" Icon={AlertCircle} tint="bg-red-50 text-red-600" accent="text-red-600" />
        <StatCard label="paid" value={fmtMoney(totals.paid)} hint="received" Icon={CheckCircle2} tint="bg-mint-100 text-mint-700" accent="text-mint-700" />
        <StatCard label="drafts" value={fmtMoney(totals.draft)} hint="not sent yet" Icon={FileText} tint="bg-lilac-100 text-lilac-700" />
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-slate-200">
        <button
          onClick={() => setTab('talent')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === 'talent' ? 'border-pink-500 text-pink-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-talent-invoices"
        >
          talent invoices
          <span className="ml-1.5 text-xs text-slate-400" data-testid="tab-talent-count">({talentTabCount})</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search deals, talent, brands…" className="pl-9 h-10 bg-white" data-testid="invoices-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          {isManager && (
          <Select value={talentId} onValueChange={setTalentId}>
            <SelectTrigger className="h-10 w-40 bg-white" data-testid="invoices-talent-filter"><SelectValue placeholder="talent" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all talent</SelectItem>
              {talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-44 bg-white" data-testid="invoices-status-filter"><SelectValue placeholder="status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">all statuses</SelectItem>
              <SelectItem value="to_invoice">to invoice</SelectItem>
              <SelectItem value="invoiced">invoiced</SelectItem>
              <SelectItem value="paid">paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:ml-auto inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button onClick={() => setInvoiceView('board')} className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm ${invoiceView === 'board' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="invoices-view-board">
            <LayoutGrid className="w-4 h-4" /> board
          </button>
          <button onClick={() => setInvoiceView('list')} className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm border-l border-slate-200 ${invoiceView === 'list' ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="invoices-view-list">
            <List className="w-4 h-4" /> list
          </button>
        </div>
      </div>

      {tab === 'talent' ? (
        <div>
          {isManager && invoiceView === 'list' && talentSuggestions.length > 0 && (
            <div className="mb-6 border border-pink-200 bg-pink-50/40 rounded-xl overflow-hidden" data-testid="talent-ready-to-invoice-section">
              <button
                onClick={() => setReadyOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-4 py-3 bg-pink-100/50 border-b border-pink-200 hover:bg-pink-100/70 transition-colors text-left"
                data-testid="talent-ready-to-invoice-toggle"
              >
                {readyOpen ? <ChevronDown className="w-4 h-4 text-pink-600" /> : <ChevronRight className="w-4 h-4 text-pink-600" />}
                <Sparkles className="w-4 h-4 text-pink-600" />
                <div className="font-medium text-pink-900 text-sm">ready to invoice</div>
                <span className="text-xs text-pink-700/80">— {talentSuggestions.length} deal{talentSuggestions.length === 1 ? '' : 's'} marked as ready to invoice, not invoiced yet</span>
              </button>
              {readyOpen && (
              <div className="divide-y divide-pink-100">
                {talentSuggestions.map((d) => {
                  const brand = brandMap[d.brand_id];
                  const talent = talentMap[d.talent_id];
                  return (
                    <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/60" data-testid={`talent-suggestion-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 truncate">{d.title}</span>
                          <span className="text-slate-400 text-xs">•</span>
                          <span className="text-sm text-slate-600 truncate">{brand?.name || '—'} × {talent?.name || '—'}</span>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">{fmtMoney(suggestedInvoiceAmount(d), d.currency)}</div>
                      <Link to={`/invoicing/new?deal_id=${d.id}`}>
                        <Button size="sm" variant="ghost" className="text-pink-700 hover:text-pink-800 hover:bg-pink-100/60">invoice →</Button>
                      </Link>
                      <button
                        onClick={() => muteTalentSuggestion(d.id)}
                        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-mint-700 px-2 py-1 rounded-md hover:bg-mint-50"
                        title="already invoiced elsewhere — mute this suggestion"
                        data-testid={`mute-talent-suggestion-${d.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> invoiced
                      </button>
                      {getContentStatusList(d).includes('awaiting_po') && (
                        <button
                          onClick={() => updateDeal(d.id, toggleContentStatus(d, 'awaiting_po'))}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                          title="toggles the 'awaiting po' content status on this deal"
                          data-testid={`talent-suggestion-awaiting-po-${d.id}`}
                        >
                          awaiting po
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}
          {invoiceView === 'board' ? (
            <InvoicingKanban q={q} talentId={talentId} canEdit={isManager} />
          ) : (
            <DealsInvoicingTable q={q} statusFilter={statusFilter} talentId={talentId} canEdit={isManager} />
          )}
        </div>
      ) : (
      <div>
      {readyClients.length > 0 && (
        <div className="mb-6 border border-pink-200 bg-pink-50/40 rounded-xl overflow-hidden" data-testid="client-ready-to-invoice-section">
          <div className="flex items-center gap-2 px-4 py-3 bg-pink-100/50 border-b border-pink-200">
            <Sparkles className="w-4 h-4 text-pink-600" />
            <div className="font-medium text-pink-900 text-sm">ready to invoice</div>
            <span className="text-xs text-pink-700/80">— {readyClients.length} client{readyClients.length === 1 ? '' : 's'} with billable work this month, not yet invoiced</span>
          </div>
          <div className="divide-y divide-pink-100">
            {readyClients.map((c) => (
              <div key={c.client_id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/60" data-testid={`client-suggestion-${c.client_id}`}>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="font-medium text-slate-800 truncate">{c.name}</span>
                </div>
                <div className="text-sm font-semibold text-slate-900">{fmtMoney(c.estimated_total, c.currency)}</div>
                <Link to={`/clients/${c.client_id}`}>
                  <Button size="sm" variant="ghost" className="text-pink-700 hover:text-pink-800 hover:bg-pink-100/60">invoice →</Button>
                </Link>
                <button
                  onClick={() => muteClientSuggestion(c)}
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-mint-700 px-2 py-1 rounded-md hover:bg-mint-50"
                  title="already invoiced elsewhere — mute this suggestion"
                  data-testid={`mute-client-suggestion-${c.client_id}`}
                >
                  <Check className="w-3.5 h-3.5" /> invoiced
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-4 py-3">invoice</th>
                <th className="text-left font-medium px-4 py-3">brand</th>
                <th className="text-left font-medium px-4 py-3">talent</th>
                <th className="text-left font-medium px-4 py-3">issued</th>
                <th className="text-left font-medium px-4 py-3">due</th>
                <th className="text-right font-medium px-4 py-3">total</th>
                <th className="text-left font-medium px-4 py-3">status</th>
                <th className="w-10"></th>
              </tr>
            </thead>            <tbody>
              {loading && <tr><td colSpan={8} className="text-center text-slate-400 py-10 text-sm">loading…</td></tr>}
              {!loading && filtered.map((inv) => {
                const brand = brandMap[inv.brand_id];
                const talent = talentMap[inv.talent_id];
                const tots = calcTotals(inv);
                const dueIn = daysUntil(inv.due_date);
                const isOverdue = inv.status === 'overdue';
                return (
                  <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/invoicing/${inv.id}`} className="font-medium text-slate-800 hover:text-mint-700">{inv.number}</Link>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {inv.client_id ? (
                        <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-pink-500" />{(inv.notes || '').replace('Monthly retainer — ', '') || 'Client'}</span>
                      ) : (
                        <>{brand?.name || '—'}</>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {talent ? (
                        <div className="flex items-center gap-2">{talent.name && <InitialsAvatar name={talent.name} size={24} />}<span className="text-slate-700">{talent.name}</span></div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{fmtDateFull(inv.issue_date)}</td>
                    <td className={`px-4 py-3 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                      {fmtDateFull(inv.due_date)}
                      {inv.status === 'sent' && dueIn !== null && dueIn >= 0 && dueIn <= 7 && (
                        <span className="ml-2 text-[11px] text-amber-700">in {dueIn}d</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtMoney(tots.total, inv.currency)}</td>
                    <td className="px-4 py-3"><StatusPill status={inv.status} statuses={statuses} /></td>
                    <td className="px-2 py-3 text-right">
                      <Link to={`/invoicing/${inv.id}`} className="text-xs text-mint-600 hover:text-mint-700">open</Link>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-400 py-10 text-sm">no invoices yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

export default Invoices;
